import { useContext, useEffect, useState } from "react";
import { useProject } from "../../../utils/hooks/useProject";
import { BiPlus } from "react-icons/bi";
import Modal from "../../../utils/components/Modal";
import Input from "../../../utils/components/Input";
import { useOutletContext, useParams } from "react-router";
import API from "../../../utils/API";
import { RequestContext } from "../../../utils/context/RequestContext";
import { confirmAlert } from "react-confirm-alert";
import AlertModal from "../../../utils/components/AlertModal";
import CascadingListItem from "./CascadingListItem";
import InformModal from "utils/components/InformModal";
import Loading from "utils/components/Loading";

const CascadingLists = () => {
  const [showModal, setShowModal] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [cascadingListName, setCascadingListName] = useState("");
  const [cascadingLists, setCascadingLists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [informModalMessage, setInformModalMessage] = useState("");

  const { project } = useProject();
  const { setRequestLoading, requestLoading } = useContext(RequestContext);
  const { translate, isEditing, onCascadingImportSuccess } = useOutletContext();

  const { id } = useParams();

  useEffect(() => {
    if (id && id !== "new") getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (requestLoading) return;

    if (!cascadingListName.trim() || !project) {
      confirmAlert({
        customUI: ({ onClose }) => (
          <AlertModal
            buttons={[
              {
                label: translate("okay"),
                onClick: onClose,
                classNames: "btn btn-primary",
              },
            ]}
            message={translate("fill_all_fields")}
          />
        ),
      });
      return false;
    }

    setIsSending(true);
    setRequestLoading(true);

    API.postData(`cascading_sets?template_id=${id}`, {
      name: cascadingListName?.trim(),
    })
      .then(() => {
        setIsSending(false);
        setRequestLoading(false);
        setCascadingListName("");
        setShowModal(false);

        getData();
      })
      .catch(() => {
        setIsSending(false);
        setRequestLoading(false);
      });
  };

  const onDelete = (id) => {
    if (!id) return false;

    confirmAlert({
      customUI: ({ onClose }) => (
        <AlertModal
          buttons={[
            {
              label: translate("cancel"),
              onClick: onClose,
              classNames: "btn btn-secondary",
            },
            {
              label: translate("delete"),
              onClick: () => {
                handleDelete(id, onClose);
              },
              classNames: "btn btn-danger",
            },
          ]}
          message={translate("delete_confirmation")}
        />
      ),
    });
  };

  const handleDelete = (id, closeModal) => {
    if (requestLoading) return;

    closeModal();

    setRequestLoading(true);

    API.deleteData(`/cascading_set/${id}`).then(() => {
      getData();
    });
  };

  const handleAddDetail = (e, setId) => {
    e.preventDefault();

    const file = e.target.files[0];

    if (file) {
      setRequestLoading(true);
      setInformModalMessage(
        translate("project_settings.location_sets.excel_import_in_progress"),
      );

      const formData = new FormData();
      formData.append("file", file);

      API.postFile(
        `/cascading_set/${setId}/import?project_id=${project?.id}`,
        formData,
      )
        .then(() => {
          getData();
          onCascadingImportSuccess && onCascadingImportSuccess();
        })
        .catch((error) => {
          console.log(error);
          setInformModalMessage(
            translate("project_settings.location_sets.excel_import_failed"),
          );
        });
    }
  };

  const getData = () => {
    if (id) {
      setIsLoading(true);

      API.getData(`cascading_sets?template_id=${id}`)
        .then(({ data: res }) => {
          setCascadingLists(
            res.data?.map((item) => ({
              id: item.id,
              title: item.name,
              subTree: [],
              hasDetail: item.is_details,
              file_path: item.file_path,
            })),
          );
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    } else setIsLoading(false);
  };

  if (id === "new") return <div className="LocationSets p-4">
    {translate("form_templates.creating_cascading_list_error")}
  </div>;

  return (
    <div className="LocationSets CascadingSet p-4">
      <div className="row">
        <div className="col-12 d-flex">
          <div
            className="d-flex align-items-center mb-4"
            role="button"
            style={{ opacity: isEditing ? 1 : 0.5, pointerEvents: isEditing ? "auto" : "none" }}
            onClick={() => {
              if (!isEditing) return;

              project?.id
                ? setShowModal(true)
                : alert(translate("select_a_project_alert"));
            }}
          >
            <BiPlus className="fs-1 bg-primary text-white rounded-circle"/>
            <span className="ms-2">
              {translate(
                "project_settings.location_sets.create_new_set_button",
              )}
            </span>
          </div>
        </div>
      </div>
      {project?.id ? (
        <div
          className="Sets"
          style={{ overflowX: "hidden", overflowY: "auto" }}
        >
          {isLoading ? (
            <Loading/>
          ) : (
            cascadingLists
              .sort((a, b) => a.id - b.id)
              .map((set) => (
                <CascadingListItem
                  key={set.id}
                  data={set}
                  handleDelete={onDelete}
                  handleAddDetail={handleAddDetail}
                />
              ))
          )}

          <Modal
            title={translate("project_settings.location_sets.create_new_set")}
            positiveButtonNode={translate("save")}
            negativeButtonNode={translate("cancel")}
            positiveButtonHandler={handleSubmit}
            modalSize="lg"
            show={showModal}
            setShow={(show) => {
              setShowModal(show);
              setCascadingListName("");
            }}
            disableButtons={isSending}
          >
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <Input
                  type="text"
                  name="locationSetName"
                  title={translate("project_settings.location_sets.set_name")}
                  placeholder={translate(
                    "project_settings.location_sets.set_name_placeholder",
                  )}
                  value={cascadingListName}
                  onChange={(e) => setCascadingListName(e.target.value)}
                />
              </div>
            </form>
          </Modal>
        </div>
      ) : (
        ``
      )}

      <InformModal
        showModal={!!informModalMessage}
        message={informModalMessage}
      />
    </div>
  );
};

export default CascadingLists;
